import * as calculatorConfig from '@/config/base-config.json';
import { FINANCING_YEAR_65, FINANCING_YEAR_75, FinancingLoanType } from '@/constants/common';
import { getMinCpfOaOrCashPercent } from '@/utils/formula';
import { generateSliderSteps } from '@/utils/generateSliderSteps';

export const PRIVATE_PURCHASE_RESALE_LABEL = {
  PrivateResalePurchase: 'Private Resale Purchase',
  PurchaseDetail: 'Purchase Details',
  PurchasePrice: 'Purchase Price',
  ValuationPrice: 'Valuation Price',
  PotentialExpenses: 'Potential Expenses',
  OptionFee: 'Option Fee',
  OptionExerciseFee: 'Option Exercise Fee',
  BSDLongForm: "Buyer's Stamp Duty",
  BSD: 'BSD',
  ABSDLongForm: "Additional Buyer's Stamp Duty",
  ABSD: 'ABSD',
  BsdAndAbsdNote:
    'BSD/ABSD has to be paid upfront in cash before reimbursement from CPF can occur if there are sufficient funds in CPF.',
  LegalFee: 'Legal Fee',
  FinancingDetails: 'Financing Details',
  LoanTenure: 'Loan Tenure (Years)',
  MortgageLoanInterestRate: 'Mortgage Loan Interest Rate',
  LoanToValue: 'Loan-to-Value',
  BreakdownOfValuationPrice: 'Breakdown of Valuation Price',
  Valuation: 'Valuation',
  Cash: 'Cash',
  CpfOaPerCash: 'CPF OA / Cash',
  LoanAmount: 'Loan Amount',
  MonthlyPayable: 'Monthly Payable',
  BothCpfAndCashUsable: 'Both CPF & Cash usable',
  Total: 'Total',
  Principal: 'Principal',
  Interest: 'Interest',
  MonthlyPayableNote: 'Estimation for the first payment month only',
  CashBreakdown: 'Cash Breakdown',
  COVLongForm: 'Cash Over Valuation',
  COV: 'COV',
  MandatoryCashTowardsValuation: 'Mandatory Cash Towards Valuation',
  TotalCashOutlay: 'Total Cash Outlay',
  DownpaymentBreakdown: 'Downpayment Breakdown',
  DepositToBePaid: 'Deposit to be paid',
  OutstandingPayablesInCash: 'Outstanding Payables in Cash',
  LessDepositIncludingCOV: 'Less Deposit, Including COV',
  OutstandingPayablesInCpfOaPerCash: 'Outstanding Payables in CPF OA / Cash',
  PurchaseTimeline: 'Purchase Timeline',
  OTPLongForm: 'Option to Purchase',
  OTP: 'OTP',
  ExercisePeriod: 'Exercise Period',
  CompletionPeriod: 'Completion Period',
  TemporaryExtensionOFStay: 'Temporary Extension Of Stay',
  Weeks: 'Weeks',
  Months: 'Months',
  Amount: 'Amount',
  ExerciseOfOTP: 'Exercise of OTP',
  ExerciseFee: 'Exercise Fee',
  SalesCompletion: 'Sales Completion',
  ActualHandover: 'Actual Handover',
  GenerateTimeline: 'Generate Timeline',
};

export const VALUATION_BREAKDOWN_LABELS = [
  PRIVATE_PURCHASE_RESALE_LABEL.Cash,
  PRIVATE_PURCHASE_RESALE_LABEL.CpfOaPerCash,
  PRIVATE_PURCHASE_RESALE_LABEL.LoanAmount,
];

export const MONTHLY_BREAKDOWN_LABELS = [
  PRIVATE_PURCHASE_RESALE_LABEL.Principal,
  PRIVATE_PURCHASE_RESALE_LABEL.Interest,
];
export const MONTHLY_BREAKDOWN_COLORS = ['#9475EB', '#F25A73'];

export const DEFAULT_LOAN_DETAILS_AGE_65 = {
  AGE: FinancingLoanType.sixtyFiveYearLoan,
  LOAN_TENURE: FINANCING_YEAR_65.LOAN_TENURE.MAX,
  LOAN_TENURE_LABEL: FINANCING_YEAR_65.LOAN_TENURE.SLIDER_LABELS,
  LOAN_TO_VALUE_MAX: FINANCING_YEAR_65.LTV.MAX,
  LOAN_TO_VALUE_DEFAULT: FINANCING_YEAR_65.LTV.DEFAULT,
  LOAN_TO_VALUE_LABEL: FINANCING_YEAR_65.LTV.SLIDER_LABELS,
};

export const DEFAULT_LOAN_DETAILS_AGE_75 = {
  AGE: FinancingLoanType.seventyFiveYearLoan,
  LOAN_TENURE: FINANCING_YEAR_75.LOAN_TENURE.MAX,
  LOAN_TENURE_LABEL: FINANCING_YEAR_75.LOAN_TENURE.SLIDER_LABELS,
  LOAN_TO_VALUE_MAX: FINANCING_YEAR_75.LTV.MAX,
  LOAN_TO_VALUE_DEFAULT: FINANCING_YEAR_75.LTV.DEFAULT,
  LOAN_TO_VALUE_LABEL: FINANCING_YEAR_75.LTV.SLIDER_LABELS,
};

export const OPTION_FEE = {
  DEFAULT: calculatorConfig.option_fee_in_percent.default,
  RANGE: generateSliderSteps(0, calculatorConfig.option_fee_in_percent.max),
  MIN: calculatorConfig.option_fee_in_percent.min,
};

export const OPTION_EXERCISE_FEE = {
  DEFAULT: calculatorConfig.option_exercise_fee_in_percent.default,
  RANGE: generateSliderSteps(0, calculatorConfig.option_exercise_fee_in_percent.max),
  MIN: calculatorConfig.option_exercise_fee_in_percent.min,
};

export const LEGAL_FEE = {
  DEFAULT: calculatorConfig.legal_fee,
};

export const MORTGAGE_INTEREST_RATE = {
  LABEL_INPUTS: generateSliderSteps(
    calculatorConfig.mortgage_interest_rate.private_resale.min,
    calculatorConfig.mortgage_interest_rate.private_resale.max,
    1,
  ),
  DEFAULT: calculatorConfig.mortgage_interest_rate.private_resale.default,
  MAX: calculatorConfig.mortgage_interest_rate.private_resale.max,
  STEP: 0.01,
};

export const CASH = {
  DEFAULT: FINANCING_YEAR_65.CASH.MIN,
  SECOND_VALUE: FINANCING_YEAR_75.CASH.MIN,
};

export const LOAN_AMOUNT = {
  DEFAULT: FINANCING_YEAR_65.LTV.MAX,
  SECOND_VALUE: FINANCING_YEAR_75.LTV.MAX,
};

export const CPF_OA_PER_CASH = {
  DEFAULT: getMinCpfOaOrCashPercent(FINANCING_YEAR_65.LTV.MAX, FINANCING_YEAR_65.CASH.MIN),
  SECOND_VALUE: getMinCpfOaOrCashPercent(FINANCING_YEAR_75.LTV.MAX, FINANCING_YEAR_75.CASH.MIN),
};

export const EXERCISE_PERIOD = {
  DEFAULT: calculatorConfig.exercise_period_in_weeks.default,
  MIN: calculatorConfig.exercise_period_in_weeks.min,
  MAX: calculatorConfig.exercise_period_in_weeks.max,
  STEP: 1,
  LABEL_INPUTS: generateSliderSteps(
    calculatorConfig.exercise_period_in_weeks.min,
    calculatorConfig.exercise_period_in_weeks.max,
  ),
};

export const COMPLETION_PERIOD = {
  DEFAULT: calculatorConfig.completion_period_in_weeks.default,
  MIN: calculatorConfig.completion_period_in_weeks.min,
  MAX: calculatorConfig.completion_period_in_weeks.max,
  STEP: 1,
  LABEL_INPUTS: generateSliderSteps(
    calculatorConfig.completion_period_in_weeks.min,
    calculatorConfig.completion_period_in_weeks.max,
  ),
};

export const TEMPORARY_EXTENSION_OF_STAY = {
  DEFAULT: calculatorConfig.temporary_extension_stay_in_months.default,
  MIN: calculatorConfig.temporary_extension_stay_in_months.min,
  MAX: calculatorConfig.temporary_extension_stay_in_months.max,
  STEP: 0.5,
  LABEL_INPUTS: generateSliderSteps(
    calculatorConfig.temporary_extension_stay_in_months.min,
    calculatorConfig.temporary_extension_stay_in_months.max,
    0.5,
  ),
};

export enum PricingMode {
  BY_PERCENTAGE = 'By Percentage',
  BY_PRICE = 'By Price',
}
